/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/aria-role */
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import qs from 'qs';
import cx from 'classnames';
import { getUrlInfo } from '../../lib/url';
import s from './../../controllers/LandingForm/landingForm.module.css';
import validateFormStyles from './../../components/ValidateForm/validateForm.module.css';
import ReturnsHelpTooltip from '../ReturnsHelpTooltip';
import LoginForm from './LoginForm';
import { orderService } from '../../services/order-service/order-service';
import {
  broadcastNavigationToLandingPage,
  updateGiftFlag,
  shouldCaptureUserEmail,
} from '../../actions/page';
import { returnItemsService } from '../../services/return-items-service/return-items-service';
import { retailerSettingsService } from '../../services/retailer-settings/retailer-settings';
import { LOCALIZED_VALIDATION_PATTERNS as DEFAULT_VALIDATION } from '../../constants/validationPatterns';
import { store } from '../../store';
import { INV_LOCALE, INV_LOCALE_REDIRECT } from '../../constants/errorCodes';
import { titan } from '../../lib/productAnalytics';
import { TITAN_EVENTS, TITAN_EXPERIMENT_IDS } from '../../constants/titan';
import DEFAULT_COPY from '../../constants/defaultCopy';
import { ReturnsTooltip } from '../../directives/NewReturnsTooltip';
import Footer from '../Footer';
import { toggleTooltip } from '../../actions/tooltip';

export const mapStateToProps = ({
  page: {
    giftOrder: isGift = false,
    initialPageLoadParams,
    locale,
    productType,
    languageCode,
    isSimpleReturns,
  } = {},
  language,
  returnsSettings: {
    [productType]: {
      validation: { input_controls: inputControls = [] } = {},
      gift_returns_enabled: giftReturnsEnabled = false,
    } = {},
  } = {},
  tooltip: { loginErrorTooltip } = {},
  claims: { enabled: claimsEnabled, settings: claimsSettings, selectedType: claimSelected },
}) => ({
  isGift,
  locale,
  productType,
  language,
  languageCode,
  inputControls,
  isSimpleReturns,
  giftReturnsEnabled,
  sideBarTitle: isGift
    ? language['returns.validate.login_sidebar_gift_title']
    : language['returns.validate.login_sidebar_title'],
  sideBarSubtitle: isGift
    ? language['returns.validate.login_gift_sidebar_subtitle']
    : language['returns.validate.login_sidebar_subtitle'],
  validateFormTitle: language['returns.validate.validate_title'],
  validateFormLoadingMessage: language['returns.validate.loading_message'],
  helpTooltipTitleText: language['returns.validate.help_tooltip_title'],
  helpTooltipDescriptionText: language['returns.validate.help_tooltip_description'],
  urlParams: Object.entries(initialPageLoadParams).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key.toLowerCase()]: value,
    }),
    {},
  ),
  defaultGiftErrorMsg: language['returns.validate.order_info_gift_view_error_text'],
  invalidZipErrorMsg: language['returns.validate.invalid_zip_tooltip_text'],
  defaultErrorMsg: isGift
    ? language['returns.validate.order_info_gift_view_error_text']
    : language['returns.validate.order_info_error_text'],
  sidebarSectionContent: language['returns.validate.sidebar_section_return_content'],
  giftCardTitle: isGift
    ? language['returns.validate.toggle_view_gift_title']
    : language['returns.validate.toggle_view_title'],
  giftCardLinkUrl: isGift
    ? language['returns.validate.toggle_view_link_gift_text']
    : language['returns.validate.toggle_view_link_text'],
  loginErrorTooltip,
  claimsEnabled,
  claimsSettings,
  claimSelected,
});

const LoginSideBar = ({
  dispatch,
  isGift,
  language,
  languageCode,
  inputControls,
  sideBarTitle,
  sideBarSubtitle,
  validateFormTitle,
  validateFormLoadingMessage,
  helpTooltipTitleText,
  helpTooltipDescriptionText,
  defaultGiftErrorMsg,
  defaultErrorMsg,
  invalidZipErrorMsg,
  urlParams,
  locale,
  productType,
  isSimpleReturns,
  sidebarSectionContent,
  giftReturnsEnabled,
  giftCardTitle,
  giftCardLinkUrl,
  loginErrorTooltip,
  toStep1 = () => {},
}) => {
  const [viewChanging, setViewChanging] = React.useState(false);
  const [showForm, setShowForm] = React.useState(true);
  const [formValues, setFormValues] = React.useState({});
  const [tooltipErrorMsg, setTooltipErrorMsg] = React.useState('');
  const [submittingOrder, setSubmittingOrder] = React.useState(false);

  const { [languageCode]: DEFAULT_LANG_COPY = {} } = DEFAULT_COPY;

  const checkValidationErrors = (orderObj) => {
    const validation = { isDataValid: true, validationMessage: '' };
    const { locale: orderLocale } = orderObj;
    const {
      validationPatterns: { [orderLocale]: customValidationRules = {} } = {},
      patternValidationEnabled,
    } = retailerSettingsService.getSettings();
    const { [productType]: { [orderLocale]: defaultValidationRules = {} } = {} } =
      DEFAULT_VALIDATION;

    const enabledCustomValidationRules = Object.keys(customValidationRules).reduce(
      (accumulator, ruleKey) => {
        if (!customValidationRules[ruleKey].validationDisabled) {
          accumulator[ruleKey] = customValidationRules[ruleKey];
        }
        return accumulator;
      },
      {},
    );

    const validationRules = {
      ...defaultValidationRules,
      ...enabledCustomValidationRules,
    };

    if (patternValidationEnabled) {
      Object.keys(orderObj).forEach((field) => {
        const { [field]: { pattern, token } = {} } = validationRules;

        if (orderObj[field] && pattern && !orderObj[field].match(new RegExp(pattern), 'i')) {
          validation.isDataValid = false;
          validation.validationMessage = token;
        }
      });
    }

    return validation;
  };

  const displayValidationError = (errorMessage) => {
    const tooltipText =
      errorMessage || (urlParams.giftview === 'true' ? defaultGiftErrorMsg : defaultErrorMsg);
    setShowForm(true);
    setSubmittingOrder(false);
    setTooltipErrorMsg(tooltipText);
    if (!loginErrorTooltip) {
      store.dispatch(toggleTooltip('loginErrorTooltip'));
    }
  };

  const startReturn = (currentOrderNumber = '') => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('path')) {
      const orderPath = params.get('path').split(',');
      titan.event(TITAN_EVENTS.EXPERIMENT_VIEWED, {
        experimentId: TITAN_EXPERIMENT_IDS.RETURNS_MULTIORDER_LOOP_LINK,
        order_id: currentOrderNumber,
        total: orderPath.length,
        checkout_id: orderPath.join(','),
        extra_properties: [
          { key: 'version', value: '1' },
          { key: 'orderPath', value: orderPath.join(',') },
          { key: 'event', value: 'order-login' },
        ],
      });
    }
    toStep1();
  };

  const checkForPrepopulatedData = (inputName) => {
    const { order = null, bzip = null, dzip = null, gift = false } = urlParams;
    if (inputName === 'number') {
      return order;
    }

    if (inputName === 'zip') {
      return gift ? dzip : bzip;
    }
    return urlParams[inputName] || null;
  };

  const allInputsPrepopulated = () => {
    const modelObject = {};
    inputControls.forEach((inputControl) => {
      inputControl.forEach(({ type, name }) => {
        if (type !== 'string_value') {
          modelObject[name] = checkForPrepopulatedData(name);
        }
      });
    });

    return Object.values(modelObject).every((value) => value !== null);
  };

  const validateOrder = async (orderObj) => {
    const { code, usertype, exception, restock_code: restockCode } = urlParams;
    const retailer = retailerSettingsService.getName();
    const {
      api_version: version = 3,
      validation: {
        zip_validation_enabled: zipValidationEnabled,
        ineligible_states: ineligibleStates = [],
      } = {},
    } = retailerSettingsService.getSettings();
    returnItemsService.clearReturn();

    const orderData = {
      ...orderObj,
      retailer,
      version,
      bzip: orderObj.bzip || null,
      email: orderObj.email || null,
      number: orderObj.number || null,
      code: code || usertype || exception || null,
      restock_code: restockCode || null,
      locale: locale || 'en_US',
    };

    if (isGift) {
      orderData.isGift = true;
    }

    const isInputDataValid = checkValidationErrors(orderData);
    if (!isInputDataValid.isDataValid) {
      return displayValidationError(isInputDataValid.validationMessage);
    }
    setSubmittingOrder(true);
    orderService.storeOrder(orderData);

    if (isSimpleReturns) {
      // Reset Order Items for simple returns
      orderService.updateItems([]);
      orderService.initSimpleReturn(orderData);
      startReturn();
      return null;
    }

    try {
      const response = await orderService.validateOrder({
        order: orderData,
        store,
        product: productType,
      });
      const {
        order_info: order,
        capture_user_email: captureUserEmail,
        error_response: {
          error_message: errorMsg,
          error_message_detail: errorMsgDetail,
          error_message_detail_code: errorMsgDetailCode,
        } = {},
      } = response.data;

      const isMultiOrderError = retailerSettingsService.getIsMultiOrder() && errorMsgDetailCode;
      const isError = order == null || order === undefined || order.length < 0 || isMultiOrderError;

      store.dispatch(shouldCaptureUserEmail(!!captureUserEmail));

      if (isError) {
        let errorMessage;
        const errorMsgDetailCodeLanguage = language[`returns.validate.${errorMsgDetailCode}`];

        if (errorMsgDetailCode != null && errorMsgDetailCodeLanguage != null) {
          errorMessage = errorMsgDetailCodeLanguage;
        }

        if (errorMsgDetailCode === INV_LOCALE) {
          errorMessage = errorMsgDetailCodeLanguage || errorMsg;
        }

        if (errorMsgDetailCode === INV_LOCALE_REDIRECT) {
          const { origin, pathname } = getUrlInfo();
          const redirectParams = qs.stringify({ ...urlParams, locale: errorMsgDetail });
          const redirectURL = `${origin}${pathname}?${redirectParams}`;
          const redirectMsg = (errorMsgDetailCodeLanguage || errorMsg).replace('$url', redirectURL);
          errorMessage = redirectMsg;
        }

        return displayValidationError(errorMessage);
      }

      // Zip validation
      if (zipValidationEnabled || ineligibleStates.length > 0) {
        const validationResponse = await orderService.validateZip({
          postalCode: order.bzip_code,
          invalidStates: ineligibleStates,
          store,
        });
        if (validationResponse) {
          startReturn();
          return returnItemsService.addOrder(order);
        }
        setTooltipErrorMsg(invalidZipErrorMsg);
        if (!loginErrorTooltip) {
          store.dispatch(toggleTooltip('loginErrorTooltip'));
        }
      }

      startReturn(order.order_number);
      returnItemsService.addOrder(order);
    } catch (e) {
      displayValidationError(isInputDataValid.validationMessage);
    }
    setSubmittingOrder(false);
    setTooltipErrorMsg('');
    if (loginErrorTooltip) {
      store.dispatch(toggleTooltip('loginErrorTooltip'));
    }
    return null;
  };

  const generatePatterns = () => {
    const inputPatterns = {};
    inputControls.forEach((inputControl) => {
      inputControl.forEach(({ type, name, patterns }) => {
        if (type !== 'string_value') {
          if (patterns) {
            inputPatterns[name] = patterns;
          }
        }
      });
    });

    return inputPatterns;
  };

  /**
   * Matches input's patterns with a given value
   * @param form - returns entry inputs
   * It has a default property for any value if the pattern did not match
   * @returns {*}
   */
  const matchPattern = (form) => {
    const matches = {};
    const inputPatterns = generatePatterns();

    Object.keys(form).forEach((formKey) => {
      const formValue = form[formKey];
      const patterns = inputPatterns[formKey] || {};

      Object.keys(patterns).forEach((patternName) => {
        const validInput = formValue.match(new RegExp(patterns[patternName]), 'im');

        if (validInput) {
          matches[patternName] = formValue;
        }
      });

      if (!Object.values(matches).length && patterns.default) {
        matches[patterns.default] = formValue;
      }
    });

    return matches;
  };

  const trimObjectStrings = (obj) =>
    Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        typeof value === 'string' ? value.trim() : value,
      ]),
    );

  const submitForm = (values) => {
    const order = matchPattern(values);
    const authorization = urlParams.token || sessionStorage.getItem('token');
    validateOrder(trimObjectStrings({ ...values, ...order, authorization }));
  };

  const getValuesFromUrl = () => {
    const order = {};
    inputControls.forEach((inputControl) => {
      inputControl.forEach(({ type, name }) => {
        if (type !== 'string_value') {
          order[name] = checkForPrepopulatedData(name);
        }
      });
    });

    return order;
  };

  const validateOrderFromUrl = () => {
    let order = {};
    const orderNumber = urlParams.order;

    if (orderNumber) {
      order.number = orderNumber;
    }

    const urlValues = getValuesFromUrl();
    order = { ...order, ...urlValues };
    setFormValues(order);

    const authorizedFlow =
      urlParams.token ||
      (sessionStorage.getItem('token') && sessionStorage.getItem('token') !== 'undefined');

    let authorization = null;

    if (authorizedFlow) {
      if (urlParams.token) sessionStorage.setItem('token', urlParams.token);
      if (urlParams.order) sessionStorage.setItem('order', urlParams.order);

      authorization =
        urlParams.token ||
        (sessionStorage.getItem('token') && sessionStorage.getItem('token') !== 'undefined'
          ? sessionStorage.getItem('token')
          : undefined);

      if (
        authorization &&
        !urlParams.order &&
        sessionStorage.getItem('order') &&
        sessionStorage.getItem('order') !== 'undefined'
      ) {
        urlParams.order = sessionStorage.getItem('order');
      }
    }

    if (
      (urlParams.init === 'true' ||
        allInputsPrepopulated() ||
        (authorization && urlParams.order)) &&
      urlParams.onboarding !== 'true'
    ) {
      setShowForm(false);
      order = {
        ...urlValues,
        number: urlParams.order,
        email: urlParams.email,
        phone: urlParams.phone,
        zip: urlParams.bzip,
        dzip: urlParams.dzip,
        mockoms: urlParams.mockoms,
        authorization,
      };

      if (urlParams.gift === 'true') {
        order.zip = urlParams.dzip;
        order.isGift = urlParams.gift === 'true';
      }

      validateOrder(order);
      setFormValues(order);
    } else {
      setShowForm(true);
    }

    if (urlParams.expired === 'true') {
      const timeoutMessage =
        language['returns.validate.session_timeout'] ||
        DEFAULT_LANG_COPY['returns.validate.session_timeout'];
      if (!timeoutMessage) return;
      setTooltipErrorMsg(timeoutMessage);
      if (!loginErrorTooltip) {
        store.dispatch(toggleTooltip('loginErrorTooltip'));
      }
    }

    if (urlParams.giftview === 'true') {
      setViewChanging(true);
      dispatch(updateGiftFlag(true));

      setTimeout(() => {
        setViewChanging(false);
      }, 400);
    }
  };

  const toggleLoginView = (event) => {
    event.preventDefault();
    setViewChanging(true);

    setTimeout(() => {
      dispatch(updateGiftFlag(!isGift));
      setViewChanging(false);
    }, 400);
  };

  React.useEffect(() => {
    orderService.setGiftReturn(false);
    validateOrderFromUrl();
    dispatch(broadcastNavigationToLandingPage());
  }, []);

  return (
    <div className={cx('login-sidebar-container', s.container)}>
      <div
        className={cx('login-sidebar-content', s.login_content, {
          [s.view_transition]: viewChanging,
        })}
      >
        {/* Title and Sub Title */}
        {showForm ? (
          <div className="login-sidebar-text">
            <h2
              className="login-sidebar-title"
              dangerouslySetInnerHTML={{ __html: sideBarTitle }}
            />
            <p
              className="login-sidebar-subtitle"
              dangerouslySetInnerHTML={{ __html: sideBarSubtitle }}
            />
          </div>
        ) : (
          <div>
            <div className={cx(validateFormStyles.returns_landing, 'row-fluid')}>
              <div className="col-md-12">
                <h2 className="returns-landing-title">{validateFormTitle}</h2>
                <h3 className="returns-landing-subtitle">{validateFormLoadingMessage}</h3>
              </div>
              {submittingOrder ? (
                <div className={cx('loader-bar', validateFormStyles.nv_progress_bar_container)}>
                  <div
                    className={cx(
                      validateFormStyles.nv_progress_bar,
                      validateFormStyles.nv_progress_bar1,
                    )}
                  />
                  <div
                    className={cx(
                      validateFormStyles.nv_progress_bar,
                      validateFormStyles.nv_progress_bar2,
                    )}
                  />
                </div>
              ) : null}
            </div>
          </div>
        )}

        <new-returns-tooltip>
          <ReturnsTooltip name="loginErrorTooltip" content={tooltipErrorMsg} closeIcon />
        </new-returns-tooltip>

        <returns-help-tooltip>
          <ReturnsHelpTooltip
            titleText={helpTooltipTitleText}
            helpText={helpTooltipDescriptionText}
          />
        </returns-help-tooltip>

        {/* Login Form */}
        <div className={cx('form-group', s.login_form)}>
          {showForm ? (
            <LoginForm
              submitForm={submitForm}
              loading={submittingOrder}
              initialValues={formValues}
              s={s}
            />
          ) : null}
        </div>

        {/* Gift View */}
        {giftReturnsEnabled ? (
          <div className={s.toggle_view_container}>
            <hr className={cx('gift-return-divider', s.divider)} />
            <h3
              className={cx('gift-return-title', s.toggle_header)}
              dangerouslySetInnerHTML={{ __html: giftCardTitle }}
              role="text"
            />
            <div className={s.toggle_link_container}>
              <a
                href="#"
                className="gift-return-link"
                target="_blank"
                onClick={toggleLoginView}
                role="button"
                onContextMenu={(e) => e.preventDefault()}
                dangerouslySetInnerHTML={{ __html: giftCardLinkUrl }}
              />
            </div>
          </div>
        ) : null}

        {/* Free sidebar section */}
        {sidebarSectionContent ? (
          <div className={s.toggle_view_container}>
            <div
              className={cx('sidebar-section-return-title', s.toggle_header)}
              dangerouslySetInnerHTML={{ __html: sidebarSectionContent }}
            />
          </div>
        ) : null}
      </div>
      <returns-footer class={s.footer}>
        <Footer />
      </returns-footer>
    </div>
  );
};

LoginSideBar.defaultProps = {
  sidebarSectionContent: undefined,
};

LoginSideBar.propTypes = {
  dispatch: PropTypes.func.isRequired,
  isGift: PropTypes.bool.isRequired,
  language: PropTypes.object.isRequired,
  languageCode: PropTypes.string.isRequired,
  inputControls: PropTypes.array.isRequired,
  sideBarTitle: PropTypes.string.isRequired,
  sideBarSubtitle: PropTypes.string.isRequired,
  validateFormTitle: PropTypes.string.isRequired,
  validateFormLoadingMessage: PropTypes.string.isRequired,
  helpTooltipTitleText: PropTypes.string.isRequired,
  helpTooltipDescriptionText: PropTypes.string.isRequired,
  urlParams: PropTypes.object.isRequired,
  locale: PropTypes.string.isRequired,
  productType: PropTypes.string.isRequired,
  defaultGiftErrorMsg: PropTypes.string.isRequired,
  defaultErrorMsg: PropTypes.string.isRequired,
  invalidZipErrorMsg: PropTypes.string.isRequired,
  toStep1: PropTypes.func.isRequired,
  isSimpleReturns: PropTypes.bool.isRequired,
  sidebarSectionContent: PropTypes.string,
  giftReturnsEnabled: PropTypes.bool.isRequired,
  giftCardTitle: PropTypes.string.isRequired,
  giftCardLinkUrl: PropTypes.string.isRequired,
  loginErrorTooltip: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps)(LoginSideBar);
